


 
import { useCallback, useEffect, useRef, useState } from "react";
import { adminDailyAttendanceApi, adminLeaveReportApi } from "../../app/api";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Check, ChevronDown, Clock3, ListFilter } from "lucide-react";
import { connectSocket, getSocket } from "../../socket/socket";
import {
  getAttendanceRefreshDelayMs,
  getTodayDateInputValue,
  isBeforeAttendanceAbsentCutoff,
} from "../../utils/dateFormat";
 
const formatLocalDate = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDateOnly = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) return formatLocalDate(date);
  if (typeof value === "string") return value.split("T")[0].split(" ")[0];
  return "";
};

const isApprovedLeave = (leave) => Number(leave?.approval_status) === 1;

const leaveCoversDate = (leave, date) => {
  const start = getDateOnly(leave?.leave_start_date);
  const end = getDateOnly(leave?.leave_end_date) || start;
  return start && end && start <= date && end >= date;
};

const normalizeName = (value = "") =>
  String(value || "").trim().replace(/\s+/g, " ").toLowerCase();

const normalizeLeaveType = (value = "") =>
  String(value || "").trim().replace(/\s+/g, " ").toLowerCase();

const isShortLeaveType = (value = "") => normalizeLeaveType(value) === "short leave";

 
 
 
 
export default function MyAttendance() {
 
 
  const [selectedDate, setSelectedDate] = useState(new Date());
 
  const [sortBy, setSortBy] = useState("")
  const [sortOpen, setSortOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const today = getTodayDateInputValue();
  const IMAGE_BASE_URL = "https://uptask.visiontrek.io";
  const navigate = useNavigate();
 
  const [records, setRecords] = useState([]);
  const [approvedLeaveUserIds, setApprovedLeaveUserIds] = useState(new Set());
  const [approvedLeaveNames, setApprovedLeaveNames] = useState(new Set());
  const [approvedLeaveTypesByUser, setApprovedLeaveTypesByUser] = useState(new Map());
  const [approvedLeaveTypesByName, setApprovedLeaveTypesByName] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);
  const liveRefreshTimerRef = useRef(null);

  const getDateString = useCallback((date = selectedDate) => {
    return formatLocalDate(date);
  }, [selectedDate]);
 
  // ✅ Fetch attendance by date
  const fetchAttendance = useCallback((date, { silent = false } = {}) => {
    if (!silent) setLoading(true);
 
    return Promise.all([
      adminDailyAttendanceApi(date),
      adminLeaveReportApi(),
    ])
      .then(([res, leaveRes]) => {
        if (res?.success && Array.isArray(res.data)) {
          setRecords(res.data);
        } else {
          setRecords([]);
        }

        const approvedLeaves = (Array.isArray(leaveRes?.data) ? leaveRes.data : [])
          .filter((leave) => isApprovedLeave(leave) && leaveCoversDate(leave, date));

        const typeByUser = new Map();
        const typeByName = new Map();
        approvedLeaves.forEach((leave) => {
          const leaveType = leave.leave_type || "";
          const userId = String(leave.employee_id || leave.user_id || "");
          const name = normalizeName(leave.employee_name || leave.name);
          if (userId) typeByUser.set(userId, leaveType);
          if (name) typeByName.set(name, leaveType);
        });

        setApprovedLeaveUserIds(
          new Set(
            approvedLeaves
              .map((leave) => String(leave.employee_id || leave.user_id || ""))
              .filter(Boolean)
          )
        );
        setApprovedLeaveNames(
          new Set(
            approvedLeaves
              .map((leave) => normalizeName(leave.employee_name || leave.name))
              .filter(Boolean)
          )
        );
        setApprovedLeaveTypesByUser(typeByUser);
        setApprovedLeaveTypesByName(typeByName);
      })
      .catch(() => {
        setRecords([]);
        setApprovedLeaveUserIds(new Set());
        setApprovedLeaveNames(new Set());
        setApprovedLeaveTypesByUser(new Map());
        setApprovedLeaveTypesByName(new Map());
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  }, []);

  const mergeLiveAttendance = useCallback((attendance) => {
    if (!attendance?.user_id) return;

    setRecords((currentRecords) => {
      const nextRecords = [...currentRecords];
      const rowIndex = nextRecords.findIndex(
        (row) => String(row.user_id) === String(attendance.user_id)
      );

      if (rowIndex >= 0) {
        nextRecords[rowIndex] = {
          ...nextRecords[rowIndex],
          ...attendance,
        };
        return nextRecords;
      }

      return [...nextRecords, attendance];
    });
  }, []);
 
 
  // ✅ Load when date changes
  // useEffect(() => {
  //   fetchAttendance(selectedDate);
  // }, [selectedDate]);
 
  useEffect(() => {
    if (selectedDate) {
      const formatted = getDateString(selectedDate);
      fetchAttendance(formatted);
    }
  }, [fetchAttendance, getDateString, selectedDate]);

  useEffect(() => {
    const socket = getSocket() || connectSocket();
    if (!socket || !selectedDate) return undefined;

    const refreshCurrentDate = (payload = {}) => {
      window.clearTimeout(liveRefreshTimerRef.current);
      const formatted = getDateString(selectedDate);

      if (payload?.date === formatted && payload?.attendance) {
        mergeLiveAttendance(payload.attendance);
      }

      liveRefreshTimerRef.current = window.setTimeout(() => {
        fetchAttendance(formatted, { silent: true });
      }, payload?.attendance ? 900 : 200);
    };

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refreshCurrentDate();
    };

    const liveEvents = [
      "attendance_updated",
      "punched_in",
      "punched_out",
      "break_started",
      "break_ended",
    ];

    liveEvents.forEach((eventName) => socket.on(eventName, refreshCurrentDate));
    window.addEventListener("focus", refreshCurrentDate);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      liveEvents.forEach((eventName) => socket.off(eventName, refreshCurrentDate));
      window.removeEventListener("focus", refreshCurrentDate);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      window.clearTimeout(liveRefreshTimerRef.current);
    };
  }, [fetchAttendance, getDateString, mergeLiveAttendance, selectedDate]);

  useEffect(() => {
    if (!selectedDate || getDateString(selectedDate) !== today) return undefined;

    let timeoutId;

    const scheduleRefresh = () => {
      const delay = getAttendanceRefreshDelayMs();
      const nextDelay = delay ?? 5 * 60 * 1000;

      timeoutId = window.setTimeout(() => {
        if (delay && document.visibilityState === "visible") {
          fetchAttendance(today, { silent: true });
        }
        scheduleRefresh();
      }, nextDelay);
    };

    if (getAttendanceRefreshDelayMs() && document.visibilityState === "visible") {
      fetchAttendance(today, { silent: true });
    }

    scheduleRefresh();

    return () => window.clearTimeout(timeoutId);
  }, [fetchAttendance, getDateString, selectedDate, today]);
 
 
 
 
 
  if (loading) {
    return (
      <div className="p-6 text-center text-gray-600">
        Loading attendance...
      </div>
    );
  }
 
 
 
 
 
 
 
 
 
 
// total_minutes ke liye
const getTotalMinutes = (row) => row.total_minutes || 0;
 
// break_minutes ke liye
const getBreakMinutes = (row) => row.break_minutes || 0;

const getProfileId = (row) =>
  row.user_id || row.id || row.userId || row.employee_user_id || row.employee_id || row.employeeId || row.emp_id;

const isSelectedDateToday = () =>
  getDateString(selectedDate) === today;

const getAttendanceStatus = (row) => {
  const rowId = String(row.user_id || row.employee_id || row.id || "");
  const rowName = normalizeName(row.name || row.employee_name);
  const matchedLeaveType =
    row.leave_type ||
    approvedLeaveTypesByUser.get(rowId) ||
    approvedLeaveTypesByName.get(rowName) ||
    "";

  if (
    Number(row.on_leave) === 1 ||
    approvedLeaveUserIds.has(rowId) ||
    approvedLeaveNames.has(rowName)
  ) {
    if (isShortLeaveType(matchedLeaveType)) {
      return {
        key: "shortLeave",
        label: "Short Leave",
        className: "bg-violet-100 text-violet-700",
      };
    }

    return {
      key: "onLeave",
      label: "On Leave",
      className: "bg-sky-100 text-sky-700",
    };
  }

  if (Number(row.status) === 1) {
    return {
      key: "present",
      label: "Present",
      className: "bg-green-100 text-green-700",
    };
  }

  if (isSelectedDateToday() && isBeforeAttendanceAbsentCutoff()) {
    return {
      key: "waiting",
      label: "Waiting for Punch",
      className: "bg-amber-100 text-amber-700",
    };
  }

  return {
    key: "absent",
    label: "Absent",
    className: "bg-red-100 text-red-700",
  };
};
 
// punch_in (HH:MM:SS) ke liye
const getPunchInSeconds = (time) => {
  if (!time) return 0;
  const [h = 0, m = 0, s = 0] = time.split(":").map(Number);
  return h * 3600 + m * 60 + s;
};
 
 
 
 
 
 
 
 
 
 
 
 
 
 
 
 
 
  const attendanceCounts = records.reduce(
    (counts, row) => {
      const status = getAttendanceStatus(row).key;
      counts.all += 1;
      if (status === "present") counts.present += 1;
      if (status === "shortLeave") counts.shortLeave += 1;
      if (status === "onLeave") counts.onLeave += 1;
      if (status === "waiting") counts.waiting += 1;
      if (status === "absent") counts.absent += 1;
      return counts;
    },
    { all: 0, present: 0, shortLeave: 0, onLeave: 0, waiting: 0, absent: 0 }
  );

  const statusTabs = [
    { value: "", label: "All", count: attendanceCounts.all },
    { value: "present", label: "Present", count: attendanceCounts.present },
    { value: "shortLeave", label: "Short Leave", count: attendanceCounts.shortLeave },
    { value: "onLeave", label: "On Leave", count: attendanceCounts.onLeave },
    { value: "waiting", label: "Waiting", count: attendanceCounts.waiting },
    { value: "absent", label: "Absent", count: attendanceCounts.absent },
  ];

  const filteredRecords = records.filter((row) => {
    if (!statusFilter) return true;
    return getAttendanceStatus(row).key === statusFilter;
  });

  const sortedRecords = [...filteredRecords].sort((a, b) => {
    // Default alphabetical
    if (!sortBy) {
      return (a.name || "")
        .toLowerCase()
        .localeCompare((b.name || "").toLowerCase());
    }
 
    let result = 0;
 
    if (sortBy === "HOURS") {
      result = getTotalMinutes(b) - getTotalMinutes(a);
    }
 
    if (sortBy === "BREAK") {
      result = getBreakMinutes(b) - getBreakMinutes(a);
    }
 
    if (sortBy === "LATE") {
      result = getPunchInSeconds(a.punch_in) - getPunchInSeconds(b.punch_in);
    }
 
    // If equal, fallback to alphabetical
    if (result === 0) {
      return (a.name || "")
        .toLowerCase()
        .localeCompare((b.name || "").toLowerCase());
    }
 
    return result;
  });
 
 
  return (
    <>
      {/* <div className="px-3 md:px-6 mt-4 ">
        <Header />
      </div> */}
 
<div className="w-full mt-3 md:mt-20 flex justify-center">
        <div className="w-full max-w-7xl rounded-2xl md:rounded-3xl bg-[#12082F] border border-white/10 shadow-2xl p-3 md:p-6">
 
          {/* HEADER */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 mb-5 md:mb-6">
 
            <h2 className="font-semibold text-sm md:text-md text-white">
              {/* All Users Attendance ({selectedDate}) */}
              All Users Attendance ({getDateString(selectedDate)})
 
            </h2>
 
           
 
{/* RIGHT: DATE FILTER + RESET */}
<div className="grid w-full grid-cols-2 gap-2 md:w-auto md:flex md:justify-center">
  <AttendanceSortDropdown
    value={sortBy}
    open={sortOpen}
    onToggle={() => setSortOpen((open) => !open)}
    onClose={() => setSortOpen(false)}
    onChange={(value) => {
      setSortBy(value);
      setSortOpen(false);
    }}
  />
 
 
 
<DatePicker
  selected={selectedDate}
  onChange={(date) => setSelectedDate(date)}
  dateFormat="yyyy-MM-dd"
  className="h-12 w-full rounded-2xl border border-white/12 bg-white/[0.08] px-4 pr-10 text-sm font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_12px_30px_rgba(0,0,0,0.18)] outline-none placeholder:text-white/40 focus:border-cyan-200/55 focus:ring-2 focus:ring-cyan-300/18 md:w-[184px]"
  calendarClassName="uptask-attendance-datepicker"
  popperClassName="uptask-attendance-popper"
  wrapperClassName="w-full md:w-auto"
  popperPlacement="bottom-start"
  showMonthDropdown
  showYearDropdown
  scrollableYearDropdown
  yearDropdownItemNumber={6}
/>
 
 
 
 
  </div>
  <button
      // onClick={() => setSelectedDate(today)}
      onClick={() => {
        setSelectedDate(new Date());
        setStatusFilter("");
      }}
      className="w-full rounded-2xl border border-white/12 bg-white/[0.08] px-4 py-3 text-xs font-black text-white/78 shadow-[0_12px_30px_rgba(0,0,0,0.16)] transition hover:bg-white/[0.14] hover:text-white md:w-auto md:text-sm"
    >
      Reset
    </button>
 
  </div>
 
          <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6 md:flex md:flex-wrap">
            {statusTabs.map((tab) => {
              const active = statusFilter === tab.value;

              return (
                <button
                  key={tab.value || "all"}
                  type="button"
                  onClick={() => setStatusFilter(tab.value)}
                  className={`rounded-2xl border px-4 py-3 text-left transition ${
                    active
                      ? "border-cyan-300/45 bg-cyan-300/14 text-cyan-50 shadow-[0_14px_34px_rgba(34,211,238,0.12)]"
                      : "border-white/10 bg-white/[0.055] text-white/72 hover:border-white/20 hover:bg-white/[0.09] hover:text-white"
                  }`}
                >
                  <span className="block text-[10px] font-black uppercase tracking-[0.16em]">
                    {tab.label}
                  </span>
                  <span className="mt-1 block text-xl font-black">{tab.count}</span>
                </button>
              );
            })}
          </div>

          {/* TABLE */}
 
          <div className="max-h-[620px] overflow-auto rounded-2xl border border-white/10 bg-white/5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
            <table className="min-w-[900px] w-full text-sm text-white">
              <thead className="sticky top-0 z-10 bg-[#20143d]/95 backdrop-blur-xl">
                <tr className="text-left text-gray-300 border-b border-white/10">
              <th className="  p-3 whitespace-nowrap text-sm">Name</th>
                <th className="p-3 whitespace-nowrap text-sm">Punch In</th>
                <th className="p-3 whitespace-nowrap text-sm">Image In</th>
<th className="p-3 whitespace-nowrap text-sm">Image Out</th>
                <th className="p-3 whitespace-nowrap text-sm">Punch Out</th>
                <th className="p-3 whitespace-nowrap text-sm">Total Hours</th>
                <th className="p-3 whitespace-nowrap text-sm">Break</th>
                <th className="p-3 whitespace-nowrap text-sm">Status</th>
                <th className="p-3 whitespace-nowrap text-sm">Early / Extra</th>
              </tr>
            </thead>
 
 
 
              <tbody>
                {sortedRecords.length > 0 ? (
                  // records.map((row, i) => (
                    // sortedRecords.map((row, i) => (
                      sortedRecords.map((row, i) => (
                    <tr key={row.user_id || i}
                    className="border-b border-white/5 hover:bg-white/10 transition"
                    >
 
                      {/* NAME */}
               
 
<td className="p-3 text-center text-sm">
	  <div
	    className="flex items-center gap-3 cursor-pointer"
	    onClick={() => navigate(`/users/${getProfileId(row)}`, { state: { user: row } })}
	  >
    {/* PROFILE PIC */}
    <div className="w-9 h-9 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center shadow">
                            {row.profile_pic ? (
                              <img
                                src={`${IMAGE_BASE_URL}${row.profile_pic}`}
                                className="w-full h-full object-cover"
                                alt="profile"
                              />
                            ) : (
                              <span className="text-gray-400">👤</span>
                            )}
                          </div>
                          <p className="font-medium hover:text-[#3BC9FC] hover:underline whitespace-nowrap">
                            {row.name || "—"}
                          </p>
                        </div>
                      </td>
 
                      {/* DATE */}
               
                      <td className="p-3 text-sm">{row.punch_in || "—"}</td>
 
                      <td className="p-3 text-sm">
                        {row.punch_in_img ? (
                          <img
                            src={`${IMAGE_BASE_URL}/${row.punch_in_img}`}
                            className="w-10 h-10 rounded cursor-pointer"
                            alt={`${row.name || "Employee"} punch in`}
                            onClick={() =>
                              setImagePreview(`${IMAGE_BASE_URL}/${row.punch_in_img}`)
                            }
                          />
                        ) : "—"}
                      </td>
 
                      <td className="p-3 text-sm">
                        {row.punch_out_img ? (
                          <img
                            src={`${IMAGE_BASE_URL}/${row.punch_out_img}`}
                            className="w-10 h-10 rounded cursor-pointer"
                            alt={`${row.name || "Employee"} punch out`}
                            onClick={() =>
                              setImagePreview(`${IMAGE_BASE_URL}/${row.punch_out_img}`)
                            }
                          />
                        ) : "—"}
                      </td>
 
                      <td className="p-3 text-sm">{row.punch_out || "—"}</td>
 
                      <td className="p-3 text-sm">
                        {row.total_minutes
                          ? `${Math.floor(row.total_minutes / 60)}h ${row.total_minutes % 60}m`
                          : "—"}
                      </td>
 
                      {/* <td className="p-3 text-sm">{row.break_minutes ?? 0}m</td> */}
                      <td className="p-3 text-sm">
  {Number(row.break_minutes) === 0
    ? "Not taken "
    : `${row.break_minutes}m`}
</td>
 
 
                      <td className="p-3 text-sm">
                        {(() => {
                          const status = getAttendanceStatus(row);

                          return (
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${status.className}`}>
                              {status.label}
                            </span>
                          );
                        })()}
                      </td>
 
                      <td className="p-3 text-xs text-sm">
                        Early: {row.early_time || "0m"} <br />
                        Extra: {row.extra_time || "0m"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center p-6 text-gray-500">
                      No Attendance Records Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
 
 
 
          </div>
          {sortedRecords.length > 5 && (
            <div className="mt-4 flex items-center justify-center">
              <span className="rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-xs font-semibold text-white/65">
                Scroll to view all {sortedRecords.length} records
              </span>
            </div>
          )}
        </div>
      </div>
 
      {/* IMAGE MODAL */}
      {imagePreview && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center"
          onClick={() => setImagePreview(null)}
        >
          <div className="bg-white p-4 rounded-xl">
            <img src={imagePreview} className="max-h-[80vh]" alt="Attendance preview" />
          </div>
        </div>
      )}
    </>
  );
}
 

const SORT_OPTIONS = [
  { value: "", label: "Filter", description: "Default name order", icon: ListFilter },
  { value: "HOURS", label: "Total Working Hours", description: "Highest hours first", icon: Clock3 },
  { value: "BREAK", label: "Total Break Duration", description: "Longest break first", icon: Clock3 },
  { value: "LATE", label: "Punch-In Time", description: "Earliest punch first", icon: Clock3 },
];

function AttendanceSortDropdown({ value, open, onToggle, onClose, onChange }) {
  const selectedOption = SORT_OPTIONS.find((option) => option.value === value) || SORT_OPTIONS[0];
  const SelectedIcon = selectedOption.icon;

  return (
    <div className="relative w-full md:w-[240px]">
      <button
        type="button"
        onClick={onToggle}
        className={`group flex h-12 w-full items-center justify-between gap-3 rounded-2xl border px-4 text-left text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_12px_30px_rgba(0,0,0,0.18)] transition ${
          open
            ? "border-cyan-200/55 bg-cyan-300/12 ring-2 ring-cyan-300/18"
            : "border-white/12 bg-white/[0.08] hover:border-cyan-200/35 hover:bg-white/[0.13]"
        }`}
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-cyan-300/12 text-cyan-100 ring-1 ring-cyan-100/12">
          <SelectedIcon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-black">{selectedOption.label}</span>
          <span className="block truncate text-[10px] font-semibold text-white/42">{selectedOption.description}</span>
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-white/54 transition ${open ? "rotate-180 text-cyan-100" : "group-hover:text-white"}`} />
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Close sort dropdown"
            className="fixed inset-0 z-[4000] cursor-default"
            onClick={onClose}
          />
          <div className="absolute left-0 right-0 top-[calc(100%+10px)] z-[4001] overflow-hidden rounded-3xl border border-white/12 bg-[#130d25] p-1.5 text-white shadow-[0_28px_80px_rgba(5,2,18,0.60),inset_0_1px_0_rgba(255,255,255,0.10)]">
            <div className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-cyan-200/70 to-transparent" />
            {SORT_OPTIONS.map((option) => {
              const isSelected = option.value === value;
              const OptionIcon = option.icon;

              return (
                <button
                  key={option.value || "default"}
                  type="button"
                  onClick={() => onChange(option.value)}
                  className={`relative flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition ${
                    isSelected
                      ? "bg-cyan-300/14 text-cyan-50 ring-1 ring-cyan-200/18"
                      : "text-white/76 hover:bg-white/[0.08] hover:text-white"
                  }`}
                >
                  <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${
                    isSelected ? "bg-cyan-300/18 text-cyan-100" : "bg-white/[0.07] text-white/54"
                  }`}>
                    <OptionIcon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-black">{option.label}</span>
                    <span className="block truncate text-[11px] font-medium text-white/42">{option.description}</span>
                  </span>
                  {isSelected && (
                    <span className="grid h-6 w-6 place-items-center rounded-full bg-cyan-300 text-[#12082f]">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}







// import { useEffect, useState } from "react";
// import { adminDailyAttendanceApi } from "../../app/api";
// import { useNavigate } from "react-router-dom";
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";




// export default function MyAttendance() {


//   const [selectedDate, setSelectedDate] = useState(new Date());

//   const [currentPage, setCurrentPage] = useState(1);
//   const recordsPerPage = 5; // show 10 records per page



//   const [sortBy, setSortBy] = useState("")
//   const today = new Date().toISOString().split("T")[0];
//   const IMAGE_BASE_URL = "https://uptask.visiontrek.io";
//   const navigate = useNavigate();

//   const [records, setRecords] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [imagePreview, setImagePreview] = useState(null);

//   // ✅ Selected Date (default = today)
//   const formatDate = (date) =>
//     typeof date === "string" ? date.split("T")[0] : "--";

//   // ✅ Fetch attendance by date
//   const fetchAttendance = (date) => {
//     setLoading(true);

//     adminDailyAttendanceApi(date)
//       .then((res) => {
//         if (res?.success && Array.isArray(res.data)) {
//           setRecords(res.data);
//         } else {
//           setRecords([]);
//         }
//       })
//       .catch(() => setRecords([]))
//       .finally(() => setLoading(false));
//   };


//   // ✅ Load when date changes
//   // useEffect(() => {
//   //   fetchAttendance(selectedDate);
//   // }, [selectedDate]);

//   useEffect(() => {
//     if (selectedDate) {
//       const formatted = selectedDate.toISOString().split("T")[0];
//       fetchAttendance(formatted);
//     }
//   }, [selectedDate]);





//   if (loading) {
//     return (
//       <div className="p-6 text-center text-gray-600">
//         Loading attendance...
//       </div>
//     );
//   }










// // total_minutes ke liye
// const getTotalMinutes = (row) => row.total_minutes || 0;

// // break_minutes ke liye
// const getBreakMinutes = (row) => row.break_minutes || 0;

// // punch_in (HH:MM:SS) ke liye
// const getPunchInSeconds = (time) => {
//   if (!time) return 0;
//   const [h = 0, m = 0, s = 0] = time.split(":").map(Number);
//   return h * 3600 + m * 60 + s;
// };












 




//   const sortedRecords = [...records].sort((a, b) => {
//     // Default alphabetical
//     if (!sortBy) {
//       return (a.name || "")
//         .toLowerCase()
//         .localeCompare((b.name || "").toLowerCase());
//     }
  
//     let result = 0;
  
//     if (sortBy === "HOURS") {
//       result = getTotalMinutes(b) - getTotalMinutes(a);
//     }
  
//     if (sortBy === "BREAK") {
//       result = getBreakMinutes(b) - getBreakMinutes(a);
//     }
  
//     if (sortBy === "LATE") {
//       result = getPunchInSeconds(a.punch_in) - getPunchInSeconds(b.punch_in);
//     }
  
//     // If equal, fallback to alphabetical
//     if (result === 0) {
//       return (a.name || "")
//         .toLowerCase()
//         .localeCompare((b.name || "").toLowerCase());
//     }
  
//     return result;
//   });
  

//   const indexOfLastRecord = currentPage * recordsPerPage;
//   const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
//   const currentRecords = sortedRecords.slice(indexOfFirstRecord, indexOfLastRecord);

//   const totalPages = Math.ceil(sortedRecords.length / recordsPerPage);




//   // useEffect(() => {
//   //   setCurrentPage(1);
//   // }, [selectedDate, sortBy]);


//   return (
//     <>
//       {/* <div className="px-3 md:px-6 mt-4 ">
//         <Header />
//       </div> */}

// <div className=" px-4 py-6 md:mt-14 flex justify-center">
//         <div className="w-full max-w-7xl rounded-3xl bg-gradient-to-br from-[#2b1a63] to-[#160f3a] shadow-2xl p-6">

//           {/* HEADER */}
//           <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">

//             <h2 className="font-semibold text-md whitespace-nowrap text-white">
//               {/* All Users Attendance ({selectedDate}) */}
//               All Users Attendance ({selectedDate?.toISOString().split("T")[0]})

//             </h2>

            

// {/* RIGHT: DATE FILTER + RESET */}
// <div className="flex  gap-2 justify-center  ">

// <select
//   value={sortBy}
//   onChange={(e) => setSortBy(e.target.value)}
//   className="
// shadow-lg
// rounded-lg
// w-[144px] py-2
// text-sm
// bg-[#F3F4F6]

// focus:outline-none
// focus:ring-1
// focus:ring-gray-200
// cursor-pointer
// "
// >
//   <option value="">Filter</option>
//   <option value="HOURS">Total Working Hours</option>
//   <option value="BREAK">Total Break Duration</option>
//   <option value="LATE">Punch-In Time</option>
// </select>
  
 

// <DatePicker
//   selected={selectedDate}
//   onChange={(date) => setSelectedDate(date)}
//   dateFormatCalendar=" "    // <-- hide the header
//   dateFormat="yyyy-MM-dd"
//   className="shadow-lg rounded-lg w-[144px] py-2 text-sm bg-[#F3F4F6] px-3 cursor-pointer"
//   calendarClassName="custom-calendar"
//   showMonthDropdown
//   showYearDropdown
//   scrollableYearDropdown
//   yearDropdownItemNumber={6}
// /> 



 
//   </div>
//   <button
//       // onClick={() => setSelectedDate(today)}
//       onClick={() => setSelectedDate(new Date())}
//       className=" px-3 py-2 rounded-lg text-sm bg-[#F3F4F6] hover:bg-gray-200 shadow-lg"
//     >
//       Reset
//     </button>

//   </div>

//           {/* TABLE */}

//           <div className="overflow-x-auto rounded-2xl bg-white/5 backdrop-blur">
//             <table className="min-w-[900px] w-full text-sm text-white">
//               <thead>
//                 <tr className="text-left text-gray-300 border-b border-white/10">
//               <th className="  p-3 whitespace-nowrap text-sm">Name</th>
//                 <th className="p-3 whitespace-nowrap text-sm">Punch In</th>
//                 <th className="p-3 whitespace-nowrap text-sm">Image In</th>
// <th className="p-3 whitespace-nowrap text-sm">Image Out</th>
//                 <th className="p-3 whitespace-nowrap text-sm">Punch Out</th>
//                 <th className="p-3 whitespace-nowrap text-sm">Total Hours</th>
//                 <th className="p-3 whitespace-nowrap text-sm">Break</th>
//                 <th className="p-3 whitespace-nowrap text-sm">Status</th>
//                 <th className="p-3 whitespace-nowrap text-sm">Early / Extra</th>
//               </tr>
//             </thead>



//               <tbody>
//                 {records.length > 0 ? (
//                   // records.map((row, i) => (
//                     // sortedRecords.map((row, i) => (
//                       currentRecords.map((row, i) => (
//                     <tr key={row.user_id || i} 
//                     className="border-b border-white/5 hover:bg-white/10 transition"
//                     >

//                       {/* NAME */}
                

// <td className="p-3 text-center text-sm">
//   <div
//     className="flex items-center gap-3 cursor-pointer"
//     onClick={() => navigate(`/users/${row.user_id}`)}
//   >
//     {/* PROFILE PIC */}
//     <div className="w-9 h-9 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center shadow">
//                             {row.profile_pic ? (
//                               <img
//                                 src={`${IMAGE_BASE_URL}${row.profile_pic}`}
//                                 className="w-full h-full object-cover"
//                                 alt="profile"
//                               />
//                             ) : (
//                               <span className="text-gray-400">👤</span>
//                             )}
//                           </div>
//                           <p className="font-medium hover:text-[#3BC9FC] hover:underline whitespace-nowrap">
//                             {row.name || "—"}
//                           </p>
//                         </div>
//                       </td>

//                       {/* DATE */}
                
//                       <td className="p-3 text-sm">{row.punch_in || "—"}</td>

//                       <td className="p-3 text-sm">
//                         {row.punch_in_img ? (
//                           <img
//                             src={`${IMAGE_BASE_URL}/${row.punch_in_img}`}
//                             className="w-10 h-10 rounded cursor-pointer"
//                             onClick={() =>
//                               setImagePreview(`${IMAGE_BASE_URL}/${row.punch_in_img}`)
//                             }
//                           />
//                         ) : "—"}
//                       </td>

//                       <td className="p-3 text-sm">
//                         {row.punch_out_img ? (
//                           <img
//                             src={`${IMAGE_BASE_URL}/${row.punch_out_img}`}
//                             className="w-10 h-10 rounded cursor-pointer"
//                             onClick={() =>
//                               setImagePreview(`${IMAGE_BASE_URL}/${row.punch_out_img}`)
//                             }
//                           />
//                         ) : "—"}
//                       </td>

//                       <td className="p-3 text-sm">{row.punch_out || "—"}</td>

//                       <td className="p-3 text-sm">
//                         {row.total_minutes
//                           ? `${Math.floor(row.total_minutes / 60)}h ${row.total_minutes % 60}m`
//                           : "—"}
//                       </td>

//                       {/* <td className="p-3 text-sm">{row.break_minutes ?? 0}m</td> */}
//                       <td className="p-3 text-sm">
//   {Number(row.break_minutes) === 0
//     ? "Not taken "
//     : `${row.break_minutes}m`}
// </td>


//                       <td className="p-3 text-sm">
//                         <span className={`px-3 py-1 rounded-full text-xs ${
//                           row.status ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
//                         }`}>
//                           {row.status ? "Present" : "Absent"}
//                         </span>
//                       </td>

//                       <td className="p-3 text-xs text-sm">
//                         Early: {row.early_time || "0m"} <br />
//                         Extra: {row.extra_time || "0m"}
//                       </td>
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td colSpan="10" className="text-center p-6 text-gray-500">
//                       No Attendance Records Found
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>



//           </div>
//           {totalPages > 1 && (
//   <div className="flex justify-center mt-6 gap-2">
//     {Array.from({ length: totalPages }, (_, i) => (
//       <button
//         key={i}
//         onClick={() => setCurrentPage(i + 1)}
//         className={`px-4 py-2 rounded-lg text-sm transition-all
//           ${
//             currentPage === i + 1
//               ? "bg-white text-black shadow-lg"
//               : "bg-white/10 text-white hover:bg-white/20"
//           }
//         `}
//       >
//         {i + 1}
//       </button>
//     ))}
//   </div>
// )}
//         </div>
//       </div>

//       {/* IMAGE MODAL */}
//       {imagePreview && (
//         <div
//           className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center"
//           onClick={() => setImagePreview(null)}
//         >
//           <div className="bg-white p-4 rounded-xl">
//             <img src={imagePreview} className="max-h-[80vh]" />
//           </div>
//         </div>
//       )}
//     </>
//   );
// }




